'use client';

import { Download, Edit, FileText, Play, Trash2 } from 'lucide-react';

import { Badge } from '@kit/ui/badge';
import { Button } from '@kit/ui/button';
import { cn } from '@kit/ui/utils';

import type { EditableDocumentWithUrl } from '../types';
import { isImageFile, isPdfFile, isVideoFile } from '../utils/file-type-utils';

interface EditableDocumentCardProps<T extends EditableDocumentWithUrl> {
  document: T;
  onView?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  showActions?: boolean;
  isReordering?: boolean;
  className?: string;
  renderBadge?: (document: T) => React.ReactNode;
}

/**
 * Editable document card with thumbnail, edit/delete actions
 *
 * @example
 * ```tsx
 * <EditableDocumentCard
 *   document={doc}
 *   onView={() => setPreviewUrl(doc.signedUrl)}
 *   onEdit={() => openEdit(doc)}
 *   onDelete={() => setDeleteDocumentId(doc.id)}
 * />
 * ```
 */
export function EditableDocumentCard<T extends EditableDocumentWithUrl>({
  document,
  onView,
  onEdit,
  onDelete,
  showActions = true,
  isReordering = false,
  className,
  renderBadge,
}: EditableDocumentCardProps<T>) {
  const isImage = isImageFile(document.mimeType);
  const isVideo = isVideoFile(document.mimeType);
  const isPdf = isPdfFile(document.mimeType);

  const handleClick = () => {
    if (onView) {
      onView();
    } else if (!isImage) {
      // Open non-images in new tab
      window.open(document.signedUrl, '_blank');
    }
  };

  const getDocumentIcon = () => {
    if (isVideo) return <Play className="h-12 w-12" />;
    return <FileText className="h-12 w-12" />;
  };

  const getTypeBadgeLabel = () => {
    if (document.documentType) {
      return document.documentType.toUpperCase().replace(/_/g, ' ');
    }
    if (isImage) return 'IMAGE';
    if (isVideo) return 'VIDEO';
    if (isPdf) return 'PDF';
    return 'FILE';
  };

  return (
    <div
      className={cn(
        'group border-border bg-card relative overflow-hidden rounded-lg border transition-shadow hover:shadow-md',
        isReordering && 'opacity-50',
        className,
      )}
    >
      {/* Thumbnail/Preview */}
      <div
        role="button"
        tabIndex={0}
        className="bg-muted relative aspect-video cursor-pointer"
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        }}
      >
        {isImage ? (
          /* eslint-disable-next-line @next/next/no-img-element, ballee-media/no-untransformed-media -- signed Supabase Storage thumbnail/preview, not a public R2 asset; cdnImageUrl would pass it through unchanged. */
          <img
            src={document.thumbnailUrl || document.signedUrl}
            alt={document.title}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="text-muted-foreground flex h-full w-full items-center justify-center">
            {getDocumentIcon()}
          </div>
        )}

        {/* Action buttons overlay */}
        {showActions && (
          <div className="absolute top-2 right-2 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
            {onEdit && (
              <Button
                variant="secondary"
                size="icon"
                className="h-7 w-7"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
              >
                <Edit className="h-4 w-4" />
              </Button>
            )}
            <Button variant="secondary" size="icon" className="h-7 w-7" asChild>
              <a
                href={document.signedUrl}
                download={document.title || document.fileName}
                onClick={(e) => e.stopPropagation()}
              >
                <Download className="h-4 w-4" />
              </a>
            </Button>
            {onDelete && (
              <Button
                variant="destructive"
                size="icon"
                className="h-7 w-7"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}

        {/* Type badge */}
        <div className="absolute bottom-2 left-2">
          {renderBadge ? (
            renderBadge(document)
          ) : (
            <Badge
              variant="secondary"
              className="bg-background/80 backdrop-blur-sm"
            >
              {getTypeBadgeLabel()}
            </Badge>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-3">
        <h4 className="truncate font-medium">{document.title}</h4>
        {document.description && (
          <p className="text-muted-foreground mt-1 line-clamp-2 text-sm">
            {document.description}
          </p>
        )}
      </div>
    </div>
  );
}
